import { ogLocale as getOgLocale } from "../../services/localeCodes";
import { resolveSiteMetadata } from "../../services/siteMetadata";
import { eventImage } from "../../services/eventMetadata";
import Head from "next/head";
import { ASSET_URL, DEBUG } from "../../config";
import { buildCanonicalUrl } from "../../components/normalizeMenuUrl";

/**
 * Meta SEO pour toutes les pages 2026.
 * Gère : title, description, OpenGraph, Twitter Card, canonical, robots,
 * keywords optionnels, et JSON-LD arbitraire via prop `jsonLd`.
 */
export default function Meta({ context, title, description, image, type, jsonLd, keywords, noindex, preloadImage, seo }) {
    ({title, description, image} = resolveSiteMetadata(context, {title, description, image: eventImage(context, image)}, seo));
    let finalImage = context.org.logo;
    if (image) finalImage = image;
    if (finalImage && !finalImage.startsWith("http")) {
        finalImage = ASSET_URL + finalImage;
    }

    let titlePrefix = context.org.name || "Unisoft";
    if (context.org.domain) titlePrefix = context.org.domain;
    const repeatsSiteName = (title || "").replace("<!-- -->", "").trim() === titlePrefix.trim();
    if (DEBUG) title = "(Dev) " + (title || "");

    const cleanTitle = (title || "").replace("<!-- -->", "");
    const fullTitle = repeatsSiteName ? cleanTitle : cleanTitle + (cleanTitle ? " | " : "") + titlePrefix;
    const siteName = context.org.name || titlePrefix;
    const ogType = type || "website";

    // URL canonique : schéma de BASE_WEB_URL + host/path courant (query retirée).
    // context.url contient déjà host+préfixe → cf. buildCanonicalUrl.
    const canonical = buildCanonicalUrl(context.org, context.url);

    const lang = context.langValue || "fr";
    const ogLocale = getOgLocale(lang)

    const robots = noindex ? "noindex,nofollow" : "index,follow";
    const jsonLdArray = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];

    return (
        <Head>
            <meta charSet="utf-8" />
            <meta name="viewport" content="width=device-width,initial-scale=1.0" />

            <title>{fullTitle}</title>
            <meta name="description" content={description} />
            {keywords && <meta name="keywords" content={keywords} />}
            <meta name="robots" content={robots} />

            {canonical && <link rel="canonical" href={canonical} />}
            {context.org.logo && <link rel="icon" href={context.org.logo} />}

            {/* Preload de l'image hero pour LCP */}
            {preloadImage && <link rel="preload" as="image" href={preloadImage} />}

            <meta property="og:title" content={cleanTitle} />
            <meta property="og:description" content={description} />
            {finalImage && <meta property="og:image" content={finalImage} />}
            <meta property="og:type" content={ogType} />
            <meta property="og:site_name" content={siteName} />
            <meta property="og:locale" content={ogLocale} />
            {canonical && <meta property="og:url" content={canonical} />}

            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={cleanTitle} />
            <meta name="twitter:description" content={description} />
            {finalImage && <meta name="twitter:image" content={finalImage} />}

            {jsonLdArray.map((ld, i) => (
                <script
                    key={i}
                    type="application/ld+json"
                    dangerouslySetInnerHTML={{ __html: JSON.stringify(ld) }}
                />
            ))}
        </Head>
    );
}
